import { dragHandler } from '@/components/tiptap-editor/tiptap-extension/drag-handle-react/helpers/dragHandler.ts';
import { normalizeNestedOptions } from '@/components/tiptap-editor/tiptap-extension/drag-handle-react/helpers/normalizeOptions.ts';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { MAX_IMAGE_WIDTH, MIN_IMAGE_WIDTH } from '@/lib/utils';
import { normalizeImportedImageSrc } from '@/lib/zy-tiptap-utils-core';
import { Editor, ResizableNodeView } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

export { normalizeImportedImageSrc };

const IMAGE_NODE_DRAG_NESTED = normalizeNestedOptions(true);

export const getCommonImageAttributes = (parentAttributes?: (() => Record<string, any>) | null) => {
    return {
        ...parentAttributes?.(),
        id: {
            default: null,
            parseHTML: (element: Element) => element.getAttribute('id') || null,
            renderHTML: (attributes: Record<string, any>) => {
                if (!attributes.id) {
                    return {};
                }
                return { id: attributes.id };
            },
        },
        renderAsHTML: {
            default: false,
            parseHTML: (element: Element) => element.getAttribute('data-render-as-html') === 'true',
            renderHTML: (attributes: Record<string, any>) => ({
                'data-render-as-html': attributes.renderAsHTML ? 'true' : 'false',
            }),
        },
        pull: {
            default: null,
            parseHTML: (element: Element) => {
                const dataPull = element.getAttribute('data-pull');
                if (dataPull) {
                    return dataPull;
                }
                const float = (element as HTMLElement).style?.float;
                if (float === 'left' || float === 'right') {
                    return float;
                }
                return null;
            },
            renderHTML: (attributes: Record<string, any>) => {
                if (!attributes.pull) {
                    return {};
                }
                return {
                    'data-pull': attributes.pull,
                    style: `float: ${attributes.pull}`,
                };
            },
        },
        aspectRatio: {
            default: null,
            parseHTML: (element: Element) => element.getAttribute('data-aspect-ratio') || null,
            renderHTML: (attributes: Record<string, any>) => ({
                'data-aspect-ratio': attributes.aspectRatio || null,
            }),
        },
        maImageNodeId: {
            default: null,
            parseHTML: (element: Element) => element.getAttribute('data-ma-image-node-id') || null,
            renderHTML: (attributes: Record<string, any>) => ({
                'data-ma-image-node-id': attributes.maImageNodeId || null,
            }),
        },
        maImageId: {
            default: null,
            parseHTML: (element: Element) => {
                const value = element.getAttribute('data-ma-image-id');
                if (!value) {
                    return null;
                }
                const parsed = Number(value);
                return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
            },
            renderHTML: (attributes: Record<string, any>) => {
                if (attributes.maImageId == null) {
                    return {};
                }
                return { 'data-ma-image-id': attributes.maImageId };
            },
        },
    };
};

export const toImageCssLength = (value: unknown): string | null => {
    if (value == null || value === '') {
        return null;
    }

    if (typeof value === 'number') {
        return `${value}px`;
    }

    if (typeof value === 'string') {
        return value;
    }

    return null;
};

export const normalizeImageWidthAndHeight = (
    width: number,
    height: number,
    aspectRatio: number,
): { normalizedWidth: number; normalizedHeight: number } => {
    if (width > MAX_IMAGE_WIDTH) {
        return { normalizedWidth: MAX_IMAGE_WIDTH, normalizedHeight: Math.round(MAX_IMAGE_WIDTH / aspectRatio) };
    }
    if (width < MIN_IMAGE_WIDTH) {
        return { normalizedWidth: MIN_IMAGE_WIDTH, normalizedHeight: Math.round(MIN_IMAGE_WIDTH / aspectRatio) };
    }
    return { normalizedWidth: width, normalizedHeight: height };
};

export interface UseImageResizableMountOptions {
    editor: Editor;
    node: any;
    getPos: () => number | undefined;
    nodeTypeName: 'image' | 'zyInlineImage';
    onResize?: (width: number, height: number) => void;
    onCommit?: (width: number, height: number) => void;
    decorations?: any;
    innerDecorations?: any;
}

export const useImageResizableMount = (options: UseImageResizableMountOptions) => {
    const { editor, node, getPos, nodeTypeName, decorations, innerDecorations } = options;
    const isEditorReadonly = useIsEditorReadonly(editor);
    const containerRef = useRef<HTMLElement>(null);
    const resizableRef = useRef<ResizableNodeView | null>(null);
    const initialNodeRef = useRef(node);
    const [isResizing, setIsResizing] = useState(false);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const el = document.createElement('img');

        const syncImgAttrs = (attrs: typeof node.attrs) => {
            el.src = normalizeImportedImageSrc(attrs, editor.state?.doc?.attrs);
            if (attrs.alt != null) {
                el.alt = attrs.alt;
            }
            if (attrs.title != null) {
                el.title = attrs.title;
            }
            const width = toImageCssLength(attrs.width);
            const height = toImageCssLength(attrs.height);
            if (width) {
                el.style.width = width;
            } else {
                el.style.removeProperty('width');
            }
            if (height) {
                el.style.height = height;
            } else {
                el.style.removeProperty('height');
            }
        };

        const commitDimensions = (width: number, height: number) => {
            const pos = getPos();
            if (pos === undefined) {
                return;
            }

            const currentNode = editor.state.doc.nodeAt(pos);
            if (!currentNode || currentNode.type.name !== nodeTypeName) {
                return;
            }

            const tr = editor.state.tr.setNodeMarkup(
                pos,
                undefined,
                {
                    ...currentNode.attrs,
                    width,
                    height,
                    aspectRatio: width / height,
                },
                currentNode.marks,
            );
            editor.view.dispatch(tr);
        };

        const commitDefaultDimensionsIfNeeded = () => {
            const attributesMissingHeightAndWidth =
                !node.attrs.width || !node.attrs.height || node.attrs.width === 'auto' || node.attrs.height === 'auto';
            const clientWidth = el.clientWidth;
            const clientHeight = el.clientHeight;
            const clientWidthAndHeightAreValid = clientWidth > 0 && clientHeight > 0;

            if (attributesMissingHeightAndWidth && clientWidthAndHeightAreValid) {
                const aspectRatio = clientWidth / clientHeight;
                const { normalizedWidth, normalizedHeight } = normalizeImageWidthAndHeight(clientWidth, clientHeight, aspectRatio);
                commitDimensions(normalizedWidth, normalizedHeight);
            }
        };

        syncImgAttrs(initialNodeRef.current.attrs);
        el.setAttribute('draggable', 'false');

        if (isEditorReadonly) {
            container.appendChild(el);
            const revealImage = () => {
                el.style.visibility = '';
            };
            el.onload = revealImage;
            el.onerror = revealImage;
            if (el.complete) {
                revealImage();
            }
            return () => {
                el.remove();
            };
        }

        const rv = new ResizableNodeView({
            element: el,
            editor,
            node: initialNodeRef.current,
            getPos,
            onResize: (width, height) => {
                el.style.width = `${width}px`;
                el.style.height = `${height}px`;
                options.onResize?.(width, height);
                setIsResizing(true);
            },
            onCommit: (width, height) => {
                commitDimensions(width, height);
                options.onCommit?.(width, height);
                setIsResizing(false);
            },
            onUpdate: updatedNode => {
                if (updatedNode.type.name !== nodeTypeName) {
                    return false;
                }
                syncImgAttrs(updatedNode.attrs);
                return true;
            },
            options: {
                directions: ['left', 'right'],
                min: {
                    width: MIN_IMAGE_WIDTH,
                },
                max: {
                    width: MAX_IMAGE_WIDTH,
                },
                preserveAspectRatio: true,
                className: {
                    container: 'zy-image-resize-root',
                    resizing: 'zy-image-resizing',
                },
            },
        });

        const dom = rv.dom as HTMLElement;
        container.appendChild(dom);
        resizableRef.current = rv;

        dom.style.visibility = 'hidden';
        dom.style.pointerEvents = 'none';
        const revealImageContainer = () => {
            dom.style.visibility = '';
            dom.style.pointerEvents = '';
        };
        el.onload = () => {
            revealImageContainer();
            commitDefaultDimensionsIfNeeded();
        };
        el.onerror = () => {
            revealImageContainer();
        };
        if (el.complete) {
            revealImageContainer();
            commitDefaultDimensionsIfNeeded();
        }

        return () => {
            setIsResizing(false);
            resizableRef.current = null;
            rv.destroy();
        };
    }, [editor, getPos, isEditorReadonly, nodeTypeName]); // eslint-disable-line react-hooks/exhaustive-deps -- Keep ResizableNodeView instance stable after mount

    useLayoutEffect(() => {
        if (isEditorReadonly) {
            const container = containerRef.current;
            const el = container?.querySelector('img');
            if (!el) {
                return;
            }
            el.src = normalizeImportedImageSrc(node.attrs, editor.state?.doc?.attrs);
            if (node.attrs.alt != null) {
                el.alt = node.attrs.alt;
            }
            const width = toImageCssLength(node.attrs.width);
            const height = toImageCssLength(node.attrs.height);
            if (width) {
                el.style.width = width;
            } else {
                el.style.removeProperty('width');
            }
            if (height) {
                el.style.height = height;
            } else {
                el.style.removeProperty('height');
            }
            return;
        }
        resizableRef.current?.update(node, decorations, innerDecorations);
    }, [decorations, editor, innerDecorations, isEditorReadonly, node]);

    return { containerRef, isResizing };
};

interface UseImageDragSurfaceOptions {
    containerRef: RefObject<HTMLElement | null>;
    editor: Editor;
    getPos: () => number | undefined;
    nodeTypeName: 'image' | 'zyInlineImage';
}

export const useImageDragSurface = ({ containerRef, editor, getPos, nodeTypeName }: UseImageDragSurfaceOptions) => {
    const isEditorReadonly = useIsEditorReadonly(editor);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const dom = container.firstChild as HTMLElement;
        if (!dom) return;

        if (isEditorReadonly) {
            dom.setAttribute('draggable', 'false');
            dom.style.cursor = '';
            return;
        }

        dom.setAttribute('draggable', 'true');
        dom.style.cursor = 'grab';

        const onDragStartFromSurface = (e: DragEvent) => {
            const pos = getPos();
            if (pos === undefined) {
                return;
            }
            const current = editor.state.doc.nodeAt(pos);
            if (!current || current.type.name !== nodeTypeName) {
                return;
            }
            dragHandler(e, editor, IMAGE_NODE_DRAG_NESTED, { node: current, pos });
        };

        dom.addEventListener('dragstart', onDragStartFromSurface);

        return () => {
            dom.removeEventListener('dragstart', onDragStartFromSurface);
        };
    }, [containerRef, editor, getPos, isEditorReadonly, nodeTypeName]);
};

export const convertImageNodeType = (editor: Editor, getPos: () => number | undefined, targetNodeType: 'block' | 'inline', attrs: any) => {
    const pos = getPos();
    if (typeof pos !== 'number') {
        return;
    }

    const inlineAttrs = (() => {
        const { pull, ...rest } = attrs ?? {}; // eslint-disable-line @typescript-eslint/no-unused-vars -- pull is not used in the inline node
        return rest;
    })();

    const state = editor.state;
    const currentNode = state.doc.nodeAt(pos);
    if (!currentNode) {
        return;
    }

    if (targetNodeType === 'inline') {
        editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + currentNode.nodeSize })
            .insertContentAt(pos, {
                type: 'paragraph',
                content: [
                    {
                        type: 'zyInlineImage',
                        attrs: inlineAttrs,
                    },
                ],
            })
            .run();
    } else if (targetNodeType === 'block') {
        const $pos = state.doc.resolve(pos);
        const imageType = state.schema.nodes.image;

        if (!imageType) {
            return;
        }

        // If the inline image is inside paragraph text, split into before/after paragraphs
        // and place the block image between them.
        if (currentNode.isInline && $pos.parent.type.name === 'paragraph') {
            const paragraphType = $pos.parent.type;
            const parentNode = $pos.parent;
            const imageOffset = $pos.parentOffset;
            const beforeContent = parentNode.content.cut(0, imageOffset);
            const afterContent = parentNode.content.cut(imageOffset + currentNode.nodeSize, parentNode.content.size);
            const replacement = [];

            if (beforeContent.size > 0) {
                replacement.push(paragraphType.create(parentNode.attrs, beforeContent));
            }
            replacement.push(imageType.create(attrs));
            if (afterContent.size > 0) {
                replacement.push(paragraphType.create(parentNode.attrs, afterContent));
            }

            const tr = state.tr.replaceWith($pos.before(), $pos.after(), Fragment.fromArray(replacement));
            editor.view.dispatch(tr.scrollIntoView());
            return;
        }

        editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + currentNode.nodeSize })
            .insertContentAt(pos, {
                type: 'image',
                attrs,
            })
            .run();
    }
};
